import { Button, Paragraph, TTranslator } from '@smartshell/ui-kit';
import cn from 'classnames';
import day from 'dayjs';

import { PermissionKey } from 'auth/types';
import { TLocalesKeys } from 'localization';
import {
  EPaymentItemType,
  EPaymentMethod,
  EPaymentStatus,
  TPayment,
  TPaymentItem,
} from 'types';

import { RefundButton } from '../RefundButton';
import s from './Sidebar.module.scss';

const getRefundStatusComponent = (
  payment: TPayment,
  isRefundEnabled: boolean,
  refundHeadroom: number,
  t: TTranslator<TLocalesKeys>,
  permissions: Set<PermissionKey> | null,
  openPostPaymentModal: () => void,
  notRefund: boolean
): JSX.Element => {
  if (notRefund && payment.method !== EPaymentMethod.POSTPAID) return <></>;
  const createdAt = day(payment.created_at);

  if (
    payment.method === EPaymentMethod.POSTPAID &&
    payment.status === EPaymentStatus.PENDING
  ) {
    return (
      <div className={cn(s.status, s.status_pending)}>
        <Paragraph>{t('payment.sidebar.status.pending.text')}</Paragraph>
        <Button
          variant="outline"
          color="orange"
          size="s"
          fullWidth
          onClick={openPostPaymentModal}
        >
          {t('payment.sidebar.status.pending.button')}
        </Button>
      </div>
    );
  }

  if (payment.status === EPaymentStatus.UNPAID) {
    return (
      <div className={cn(s.status, s.status_unpaid)}>
        <Paragraph>{t('unpaid')}</Paragraph>
      </div>
    );
  }

  if (payment.is_refunded) {
    return (
      <div className={s['refund-status']}>
        {t('payment.refunded.at', createdAt.format(`D MMM YYYY HH:mm`))}
      </div>
    );
  }

  if (!isRefundEnabled) {
    return (
      <div className={s['refund-status']}>{t('payment.refund.disabled')}</div>
    );
  }

  if (refundHeadroom <= day().diff(createdAt, 'minutes')) {
    return (
      <div className={s['refund-status']}>{t('payment.refund.toLongAgo')}</div>
    );
  }

  return permissions?.has('refund_create') ? (
    <RefundButton
      payment={payment}
      bordered
      disabled={!permissions?.has('refund_create')}
    />
  ) : (
    <></>
  );
};

const getItemTitle = (
  item: TPaymentItem,
  t: TTranslator<TLocalesKeys>
): string => {
  switch (item.type) {
    case EPaymentItemType.DEPOSIT:
      return t('payments.category.deposit');
    case EPaymentItemType.BONUS:
      return t('payments.category.bonuses');
    default:
      return item.entity.title;
  }
};

export { getRefundStatusComponent, getItemTitle };
